import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { verifyEmailRequest, clearError } from '../../store/actions/authActions'
import { Card, CardContent } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { CheckCircle2, XCircle, Info, MailCheck, ArrowRight, Loader2, Sparkles } from 'lucide-react'

export const EmailVerificationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isLoading, error, emailVerified } = useSelector((state: RootState) => state.auth)
  const [countdown, setCountdown] = useState(5)
  const hasDispatched = useRef(false)

  useEffect(() => {
    if (token && !hasDispatched.current && !isLoading && !error && !emailVerified) {
      hasDispatched.current = true
      dispatch(verifyEmailRequest(token))
    }
  }, [token, dispatch, isLoading, error, emailVerified])

  useEffect(() => {
    if (emailVerified) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            dispatch(clearError())
            navigate('/subscription-pricing')
            return 0
          }
          return prev - 1
        })
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [emailVerified, navigate, dispatch])

  const handleSignInClick = () => {
    dispatch(clearError())
    navigate('/subscription-pricing')
  }

  const isAlreadyVerified = error?.toLowerCase().includes('already verified')

  return (
    <div className="min-h-screen bg-[#FFF7ED] relative flex items-center justify-center p-6 overflow-hidden font-sans">
      {/* Dynamic Background Elements - Using Application Theme Colors */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-orange-200/40 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-orange-300/30 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s rotate(180deg)' }} />

      <Card className="w-full max-w-md border-orange-100 bg-white shadow-[0_20px_50px_rgba(249,115,22,0.1)] relative overflow-hidden group rounded-3xl">
        {/* Decorative Top Gradient Line - Matching Primary Branding */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600" />

        <CardContent className="pt-14 pb-12 px-10">
          <div className="flex flex-col items-center text-center space-y-8">

            {/* Logo Section */}
            <div className="relative">
              <Link to="/" className="flex items-center gap-3">
                <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-48 w-auto" />
              </Link>
            </div>

            {/* Content Logic */}
            {isLoading && (
              <div className="space-y-6 animate-in fade-in zoom-in duration-500 w-full">
                <div className="flex justify-center py-4">
                  <div className="relative">
                    <Loader2 className="w-14 h-14 text-orange-500 animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-orange-600 rounded-full" />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-heading">Securing Account</h2>
                  <p className="text-gray-500 leading-relaxed">Please wait while we validate your secure token and activate your profile.</p>
                </div>
              </div>
            )}

            {emailVerified && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 w-full">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto ring-8 ring-green-100/50">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-heading">Success!</h2>
                  <p className="text-gray-500 leading-relaxed">Your email is verified. Access to DocSprintAI has been successfully granted.</p>
                </div>
                <div className="py-4 px-6 bg-orange-50 rounded-2xl border border-orange-100 shadow-inner">
                  <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
                    Auto-redirecting in <span className="text-orange-600 font-bold text-xl">{countdown}s</span>
                  </p>
                </div>
                <div className="block w-full">
                  <Button
                    onClick={handleSignInClick}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white h-14 rounded-2xl font-bold shadow-[0_10px_20px_-5px_rgba(249,115,22,0.4)] transition-all hover:-translate-y-1 flex items-center justify-center gap-2 group/btn"
                  >
                    Continue<ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            )}

            {error && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 w-full">
                <div className={`w-20 h-20 ${isAlreadyVerified ? 'bg-blue-50' : 'bg-red-50'} rounded-full flex items-center justify-center mx-auto ring-8 ${isAlreadyVerified ? 'ring-blue-100/50' : 'ring-red-100/50'}`}>
                  {isAlreadyVerified ? (
                    <Info className="w-12 h-12 text-blue-500" />
                  ) : (
                    <XCircle className="w-12 h-12 text-red-500" />
                  )}
                </div>

                <div className="space-y-3">
                  <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-heading">
                    {isAlreadyVerified ? 'Verified' : 'Oh No!'}
                  </h2>
                  <p className="text-gray-500 leading-relaxed">
                    {isAlreadyVerified
                      ? 'This email address has already been confirmed. You can proceed to sign in.'
                      : error}
                  </p>
                </div>

                <div className="flex flex-col gap-4 pt-4">
                  <div className="w-full">
                    <Button
                      onClick={handleSignInClick}
                      className="w-full bg-gray-900 hover:bg-black text-white h-14 rounded-2xl font-bold transition-all shadow-lg"
                    >
                      Continue
                    </Button>
                  </div>
                  {!isAlreadyVerified && (
                    <Link to="/signup" className="text-orange-600 font-semibold hover:text-orange-700 transition-colors text-sm py-2">
                      Get a new verification link
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Footer Branding - Soft & Elegant */}
      <Link to="/" className="absolute bottom-12 flex items-center gap-3 grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all cursor-default">
        <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-16 w-auto" />
      </Link>
    </div>
  )
}
